import { Capacitor } from "@capacitor/core";
import { InAppReview } from "@capacitor-community/in-app-review";
import { Elements } from "@stripe/react-stripe-js";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { TailSpin } from "react-loader-spinner";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  CURRENT_ORDER_QUERY_KEY,
  invalidateCurrentOrderDetails,
} from "@/modules/cart/useCartDetails";
import { get, post } from "api";
import { Models, RewardStatus, ShopDisplayType } from "api/types";
import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import CartCheckIcon from "atoms/icon/icons/ic_added_to_cart_check_ol.svg";
import RepeatCash from "atoms/icon/icons/RepeatCash.svg";
import WarningIcon from "atoms/icon/icons/warning_circle_info.svg";
import { AnimatedBackground } from "components/AnimatedBackground";
import { stripeLoader, useStripePublishableKey } from "modules/stripe";
import { TreatmentOrderedList } from "organisms/treatment-ordered-list/TreatmentOrderedList";
import { queryClient } from "pages/Root";
import { useFeatureFlag } from "services/Flags";
import { getProductTitle } from "services/HelperService";
import { notNull } from "toolbox/Guards";
import {
  formatBillingInterval,
  getBillingIntervalBase,
  parseBillingPeriodName,
} from "utils/billingPeriod.helpers";
import { orderContainsSelectableRewards } from "utils/membershipRewards";

type CheckoutSuccessContainerProps = {
  platform: Models<"PlatformModel">;
  rewards: Models<"RewardModel">[];
  primaryCta?: {
    text: string;
    link: string;
  };
  errorCta: {
    text: string;
    link: string;
  };
};

export const CheckoutSuccessContainer = ({
  platform,
  rewards,
  primaryCta,
  errorCta = {
    text: "Back to cart",
    link: "/cart",
  },
}: CheckoutSuccessContainerProps) => {
  const stripeKey = useStripePublishableKey();

  const stripePromise = stripeLoader.getStripe(stripeKey);

  return (
    <div className="CheckoutSuccessContainer">
      <Elements stripe={stripePromise}>
        <CheckoutSuccessPageInternal
          rewards={rewards}
          platform={platform}
          primaryCta={primaryCta}
          errorCta={errorCta}
        />
      </Elements>
    </div>
  );
};

const CheckoutSuccessPageInternal = ({
  rewards,
  platform,
  primaryCta,
  errorCta,
}: CheckoutSuccessContainerProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const shopDisplayOrderId = searchParams.get("shopDisplayOrderId");
  const shopDisplayOrderTransactionId = searchParams.get("orderTransactionId");
  const [errorLoadingTransaction, setErrorLoadingTransaction] = useState(false);

  const isFlexibleMembershipsEnabled = useFeatureFlag(
    "flexible_membership_terms"
  );
  const isSelectMembershipRewardsAfterCheckoutEnabled = useFeatureFlag(
    "selectMembershipRewardsAfterCheckout"
  );
  const isV3Enabled = true;

  enum orderStatus {
    paid = "Paid",
    processing = "Processing",
    failed = "Failed",
    requiresAction = "RequiresAction",
  }

  const renderGiftCardForSelfSuccessButton = () => {
    return (
      <div className="sticky bottom-[env(safe-area-inset-bottom)] mb-8 w-full p-4 pb-5">
        <Button
          fullWidth
          onClick={() => {
            if (platform.platformSettings?.spendBasedRewardsEnabled) {
              navigate(
                `/cart/checkout/reward?rewardProgress=${rewardProgress}&shopDisplayOrderId=${
                  shopDisplayOrderId || ""
                }`
              );
            } else {
              navigate("/home");
            }
          }}
        >
          Continue
        </Button>
      </div>
    );
  };

  const renderErrorButton = () => (
    <div className="py-32">
      <Button fullWidth onClick={() => navigate(errorCta.link)}>
        {errorCta.text}
      </Button>
    </div>
  );

  const email = searchParams.get("email");
  useEffect(() => {
    if (email != null) {
      post("/user/update", { email });
    }
  }, [email]);

  const { data: shopDisplayOrderTransaction } = useQuery({
    enabled: !!shopDisplayOrderTransactionId,
    queryKey: [
      "/orders/transaction/{shopDisplayOrderTransactionId}",
      { shopDisplayOrderTransactionId: shopDisplayOrderTransactionId },
    ],
    queryFn: () =>
      get("/orders/transaction/{shopDisplayOrderTransactionId}", {
        shopDisplayOrderTransactionId: shopDisplayOrderTransactionId!,
      }),
    onError: () => {
      console.log("Error loading transaction");
      setErrorLoadingTransaction(true);
    },
    refetchInterval: (transaction, { state: { status } }) => {
      if (status === "error") return false;
      if (transaction?.status === "Failed" || transaction?.status === "Paid")
        return false;
      return 1000;
    },
    refetchOnMount: true,
  });

  const { data: giftCardTransfers } = useQuery({
    queryKey: [
      "/gift-cards/transfers/by-order/{orderId}",
      { orderId: shopDisplayOrderId },
    ],
    queryFn: () =>
      get("/gift-cards/transfers/by-order/{orderId}", {
        orderId: shopDisplayOrderId ?? "",
      }),
    onError: () => {
      console.log("Error loading gift card transfers");
    },
    refetchInterval: (giftCardTransfers) => {
      if (giftCardTransfers !== undefined) {
        return false;
      }

      return 1000;
    },
    refetchOnMount: true,
  });

  const { data: shopDisplayOrder } = useQuery({
    queryKey: [
      "/orders/{shopDisplayOrderId}",
      { shopDisplayOrderId: shopDisplayOrderId },
    ],
    queryFn: () =>
      get("/orders/{shopDisplayOrderId}", {
        shopDisplayOrderId: shopDisplayOrderId || "",
      }),
    refetchInterval: (shopDisplayOrder) =>
      shopDisplayOrder &&
      (shopDisplayOrder?.status == orderStatus.failed ||
        shopDisplayOrder?.status == orderStatus.paid)
        ? false
        : 1000,
    refetchOnMount: true,
  });

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    if (
      (shopDisplayOrder?.status == orderStatus.paid &&
        shopDisplayOrderTransaction?.status == "Paid") ||
      !shopDisplayOrderTransactionId
    ) {
      InAppReview.requestReview();
    }
  }, [
    orderStatus.paid,
    shopDisplayOrder?.status,
    shopDisplayOrderTransaction?.status,
    shopDisplayOrderTransactionId,
  ]);

  const getTitle = (item: Models<"ShopDisplayOrderLineItemModel">): string => {
    if (item.displayType === ShopDisplayType.GiftCard && item.cost) {
      return `$${Math.floor(item.cost / 100)} Gift Card`;
    }

    return getProductTitle(item.productName, item.name);
  };

  const getSessionText = (
    item: Models<"ShopDisplayOrderLineItemModel">,
    giftCardTransfers: Models<"DisplayGiftCardTransferItem">[]
  ): string => {
    if (item.displayType === ShopDisplayType.GiftCard && item.cost) {
      const itemGiftCardTransfer = giftCardTransfers?.find(
        (transfer) => item.packageTransferId === transfer.id
      );

      if (
        itemGiftCardTransfer &&
        itemGiftCardTransfer.senderPlatformUserId !==
          itemGiftCardTransfer.recipientPlatformUserId
      ) {
        return `To: ${itemGiftCardTransfer.recipientInformation}`;
      }

      return " ";
    }

    return `${item.quantity} ${
      item.quantity > 1
        ? item.package?.packageUnitType.pluralUnitType ?? "units"
        : item.package?.packageUnitType.singularUnitType ?? "unit"
    }`;
  };

  const orderedTreatments = shopDisplayOrder?.shopDisplayOrderLineItems
    ?.filter(notNull)
    .map((item) => {
      let displayType;

      const itemGiftCardTransfer = giftCardTransfers?.find(
        (transfer) => item.packageTransferId === transfer.id
      );

      switch (item.displayType) {
        case ShopDisplayType.Package:
          displayType = "Package";
          break;
        case ShopDisplayType.Treatment:
          displayType = "Treatment";
          break;
        case ShopDisplayType.Membership:
          displayType = "Membership";
          break;
        case ShopDisplayType.GiftCard:
          displayType = "GiftCard";
          break;
        default:
          displayType = "Product";
      }
      if (item.name && item.shopDisplayMembershipId == null) {
        return {
          id: item.id,
          imgUrl: item.imageUrl,
          type: displayType,
          title: getTitle(item),
          session: getSessionText(item, giftCardTransfers || []),
          packageTransfer: itemGiftCardTransfer || undefined,
        };
      }
    })
    .filter(notNull);

  const orderedPackagesAndTreatments =
    orderedTreatments?.filter(
      (item) => item.type === "Package" || item.type === "Treatment"
    ) || [];

  const orderedGiftCards =
    orderedTreatments?.filter((item) => item.type === "GiftCard") || [];

  const orderedGiftCardsForSelf =
    orderedTreatments?.filter(
      (item) =>
        item.type === "GiftCard" &&
        item.packageTransfer != null &&
        item.packageTransfer.senderPlatformUserId ===
          item.packageTransfer.recipientPlatformUserId
    ) || [];

  const orderedGiftCardsForOthers =
    orderedTreatments?.filter(
      (item) =>
        item.type === "GiftCard" &&
        item.packageTransfer != null &&
        item.packageTransfer.senderPlatformUserId !==
          item.packageTransfer.recipientPlatformUserId
    ) || [];

  const isMultipleRecipientsForGiftCards = (() => {
    const recipients = new Set(
      (orderedTreatments ?? [])
        .filter((item) => item.type === "GiftCard" && item.packageTransfer)
        .map((item) => item.packageTransfer!.recipientPlatformUserId)
    );

    return recipients.size > 1;
  })();

  const orderedProducts =
    orderedTreatments?.filter(
      (item) =>
        item.type === "Product" || item.type === "ProductFromExternalSource"
    ) || [];

  const orderedMemberships = shopDisplayOrder?.shopDisplayOrderLineItems
    ?.filter(notNull)
    .map((item) => {
      if (item.name && item.imageUrl && item.shopDisplayMembershipId != null) {
        const billingPeriodInterval = item.membershipBillingPeriodInterval ?? 1;
        const intervalCount = item.membershipBillingPeriodIntervalCount ?? 1;
        const billingPeriodName = parseBillingPeriodName(billingPeriodInterval);
        const baseInterval = getBillingIntervalBase(billingPeriodName);

        const billingPeriodNameCapitalized =
          baseInterval.charAt(0).toUpperCase() + baseInterval.slice(1);

        const formattedInterval = formatBillingInterval(
          billingPeriodNameCapitalized,
          intervalCount
        );

        const parsedBillingPeriodInterval = isFlexibleMembershipsEnabled
          ? ` Every ${formattedInterval}`
          : "Monthly";

        return {
          id: item.id,
          imgUrl: item.imageUrl,
          type: "Membership",
          title: item.name,
          session: `Billed ${parsedBillingPeriodInterval}`,
        };
      }
    })
    .filter(notNull);

  const futureRewards = rewards.filter(
    (r) => r.status === RewardStatus.NotAvailable
  );
  const nextReward = futureRewards.length
    ? futureRewards.reduce(
        (acc, curr) =>
          acc.minimumVisits ?? 0 <= (curr.minimumVisits ?? 0) ? acc : curr,
        futureRewards[0]
      )
    : undefined;
  const rewardProgress = nextReward?.unlockProgressPercent || 0;

  if (
    errorLoadingTransaction ||
    shopDisplayOrderTransaction?.status == "Failed"
  ) {
    queryClient.invalidateQueries([
      "/orders/{shopDisplayOrderId}",
      { shopDisplayOrderId: shopDisplayOrderId },
    ]);

    const errorContent = (
      <div className="content-center items-center justify-center p-4 text-center">
        <div className="flex items-center justify-center gap-2 py-12">
          <Icon svg={WarningIcon} size="medium" color="primaryText" />
        </div>
        <div className="pb-12 font-serif text-h2">Sorry!</div>
        <br />
        <div className="font serif text-h3">
          Something went wrong. Please try your payment again.
        </div>

        {renderErrorButton()}
      </div>
    );

    return isV3Enabled ? (
      <AnimatedBackground className="min-h-screen">
        {errorContent}
      </AnimatedBackground>
    ) : (
      errorContent
    );
  } else if (
    (shopDisplayOrder?.status == orderStatus.paid &&
      shopDisplayOrderTransaction?.status == "Paid") ||
    !shopDisplayOrderTransactionId
  ) {
    queryClient.invalidateQueries([
      "/orders/{shopDisplayOrderId}",
      { shopDisplayOrderId: shopDisplayOrderId },
    ]);
    invalidateCurrentOrderDetails();

    const isGiftCardForItself =
      orderedGiftCards.length > 0 &&
      orderedGiftCards.every(
        (gc) =>
          gc.packageTransfer != null &&
          gc.packageTransfer.senderPlatformUserId ===
            gc.packageTransfer.recipientPlatformUserId
      );

    const shouldRenderGiftCardSuccess =
      isGiftCardForItself &&
      orderedGiftCards.length > 0 &&
      orderedPackagesAndTreatments.length === 0 &&
      (orderedMemberships?.length || 0) === 0 &&
      orderedProducts.length === 0;

    if (shouldRenderGiftCardSuccess) {
      const totalGiftCardAmount =
        shopDisplayOrder?.shopDisplayOrderLineItems
          ?.filter(notNull)
          .filter((item) => item.displayType === ShopDisplayType.GiftCard)
          .reduce((total, item) => total + (item.cost || 0), 0) || 0;

      const giftCardContent = (
        <div className="flex h-screen flex-col">
          <div className="mt-[env(safe-area-inset-top)] flex flex-col items-center pt-12 text-center">
            <div className="flex items-center justify-center py-12">
              <Icon svg={CartCheckIcon} size="xlarge" color="primaryText" />
            </div>
            <div className="pb-8 font-serif text-h2">
              <div>Treat yourself!</div>
            </div>
            <div className="pb-4 text-body1 text-primary">
              Your gift card has been converted to
              <br />
              RepeatCash and can be used right away!
            </div>
            <div className="flex items-center justify-center gap-2 pb-8">
              <Icon svg={RepeatCash} size="large" />
              <span className="text-2xl font-bold">
                ${Math.floor(totalGiftCardAmount / 100)}
              </span>
            </div>
          </div>
          <div className="flex-1" />
          {renderGiftCardForSelfSuccessButton()}
        </div>
      );

      return isV3Enabled ? (
        <AnimatedBackground className="min-h-screen">
          {giftCardContent}
        </AnimatedBackground>
      ) : (
        <div className="bg-one">{giftCardContent}</div>
      );
    }

    const handlePrimaryButtonClick = async () => {
      if (primaryCta) {
        navigate(primaryCta.link);
        return;
      }

      if (
        isSelectMembershipRewardsAfterCheckoutEnabled &&
        shopDisplayOrder &&
        (await orderContainsSelectableRewards(shopDisplayOrder))
      ) {
        navigate(
          `/cart/checkout/membership-rewards?shopDisplayOrderId=${
            shopDisplayOrderId || ""
          }&rewardProgress=${rewardProgress}`
        );
        return;
      }

      if (platform.platformSettings?.spendBasedRewardsEnabled) {
        navigate(
          `/cart/checkout/reward?rewardProgress=${rewardProgress}&shopDisplayOrderId=${
            shopDisplayOrderId || ""
          }`
        );
      } else {
        navigate("/home");
      }
    };

    const successContent = (
      <div className="flex h-screen flex-col pt-[env(safe-area-inset-top)] text-center">
        <div className="flex items-center justify-center py-12">
          <Icon svg={CartCheckIcon} size="xlarge" color="primaryText" />
        </div>
        <div className="pb-8 font-serif text-h2">
          <div>Get excited,</div>
          <div>your order is confirmed!</div>
        </div>
        <div className="flex flex-1 flex-col gap-4">
          <div className="px-4">
            <TreatmentOrderedList
              treatments={[
                ...(orderedMemberships || []),
                ...orderedGiftCardsForSelf,
                ...orderedGiftCardsForOthers,
                ...orderedPackagesAndTreatments,
                ...orderedProducts,
              ]}
            ></TreatmentOrderedList>
          </div>
          <div className="grow" />
          <div className="sticky bottom-[env(safe-area-inset-bottom)] w-full p-4 pb-5 empty:hidden">
            <Button fullWidth onClick={handlePrimaryButtonClick}>
              {primaryCta?.text ?? "Continue"}
            </Button>
          </div>
        </div>
      </div>
    );

    return isV3Enabled ? (
      <AnimatedBackground className="min-h-screen">
        {successContent}
      </AnimatedBackground>
    ) : (
      <div className="bg-one">{successContent}</div>
    );
  } else {
    // Processing
    const processingContent = (
      <div className="flex h-screen flex-col items-center justify-center gap-12 px-8">
        <div className="flex flex-col items-center gap-8">
          <div className="relative">
            <div className="bg-brand/10 absolute inset-0 animate-pulse rounded-full blur-2xl" />
            <TailSpin
              height="80"
              width="80"
              radius="0"
              color="rgb(var(--repeatmd-brand-color))"
            />
          </div>
          <div className="text-center" aria-live="polite">
            <div className="font-serif text-h1 leading-tight tracking-tight">
              Confirming your order
            </div>
            <div className="mt-4 text-body1 text-primary/60">
              This will only take a moment.
            </div>
          </div>
        </div>
      </div>
    );

    return isV3Enabled ? (
      <AnimatedBackground className="min-h-screen">
        {processingContent}
      </AnimatedBackground>
    ) : (
      processingContent
    );
  }
};
